export const BASE_APPEARANCE_SLOT_ORDER = [0, 1, 2, 3, 4, 5, 6] as const;
export const BODY_COLOR_CHANNELS = ['hair', 'torso', 'legs', 'feet', 'skin'] as const;

export type BaseAppearanceSlot = typeof BASE_APPEARANCE_SLOT_ORDER[number];
export type BodyColorChannel = typeof BODY_COLOR_CHANNELS[number];

export interface BaseCharacterAppearance {
  gender: 'male';
  kitByAppearanceSlot: Record<string, number>;
  bodyColors: Record<BodyColorChannel, number>;
  viewerColors?: Partial<Record<BodyColorChannel, string>>;
}

export interface CharacterColorSwap {
  from: number;
  to: number;
}

export interface BodyColorSwatch {
  value: number;
  color: string;
}

const MALE_LOOK_OPTIONS: Record<BaseAppearanceSlot, number[]> = {
  0: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 129, 130, 131, 132, 133, 134, 144, 145, 146, 147, 148, 149, 150, 151, 201, 202, 203, 204, 205, 206, 207, 208, 209, 210, 211, 212, 213, 214, 215, 216, 217, 218, 219, 220, 221, 222, 223, 224, 225, 226, 227, 228, 229, 230, 231, 232, 233, 234, 235, 236, 237, 238, 239, 240, 241, 242, 243, 244, 245, 246, 247],
  1: [10, 11, 12, 13, 14, 15, 16, 17, 111, 112, 113, 114, 115, 116, 117],
  2: [18, 19, 20, 21, 22, 23, 24, 25, 105, 106, 107, 108, 109, 110, 254, 255, 256, 257, 258, 259],
  3: [26, 27, 28, 29, 30, 31, 32, 84, 85, 86, 87, 88, 260, 261, 262, 263, 264],
  4: [33, 34],
  5: [36, 37, 38, 39, 40, 41, 100, 101, 102, 103, 104, 281, 282, 283, 284, 285, 286, 287, 288, 289, 290, 291],
  6: [42, 43],
};

const STAFF_ONLY_LOOK_OPTIONS: Partial<Record<BaseAppearanceSlot, number[]>> = {
  // Kit 82 is a mine cart model in the feet slot. Keep it renderable for the
  // hidden staff profile toggle, but never offer it in public character creation.
  6: [82],
};

const BODY_COLOR_OPTIONS: Record<BodyColorChannel, number[]> = {
  hair: [20, 19, 10, 18, 4, 5, 15, 7, 0, 6, 21, 9, 22, 17, 8, 16, 11, 24, 23, 3, 2, 1, 14, 13, 12],
  torso: [24, 23, 2, 22, 12, 11, 6, 19, 4, 0, 9, 13, 25, 8, 15, 26, 21, 7, 20, 14, 10, 28, 27, 3, 5, 18, 17, 1, 16],
  legs: [26, 24, 23, 3, 22, 13, 12, 7, 19, 5, 1, 10, 14, 25, 9, 0, 21, 8, 20, 15, 11, 28, 27, 4, 6, 18, 17, 2, 16],
  feet: [0, 1, 2, 3, 4, 5],
  skin: [7, 6, 5, 4, 3, 2, 1, 0],
};

const GAME0_SOURCE_BODY_COLORS = [6798, 8741, 25238, 4626, 4550];
const GAME0_DESTINATION_BODY_COLORS = [
  [6798, 107, 10283, 16, 4797, 7744, 5799, 4634, -31839, 22433, 2983, -11343, 8, 5281, 10438, 3650, -27322, -21845, 200, 571, 908, 21830, 28946, -15701, -14010],
  [8741, 12, -1506, -22374, 7735, 8404, 1701, -27106, 24094, 10153, -8915, 4783, 1341, 16578, -30533, 25239, 8, 5281, 10438, 3650, -27322, -21845, 200, 571, 908, 21830, 28946, -15701, -14010],
  [25238, 8742, 12, -1506, -22374, 7735, 8404, 1701, -27106, 24094, 10153, -8915, 4783, 1341, 16578, -30533, 8, 5281, 10438, 3650, -27322, -21845, 200, 571, 908, 21830, 28946, -15701, -14010],
  [4626, 11146, 6439, 12, 4758, 10270],
  [4550, 4537, 5681, 5673, 5790, 6806, 8076, 4574],
] as const;
const GAME0_SOURCE_SKIN_COLORS = [-10304, 9104, -1, -1, -1];
const GAME0_DESTINATION_SKIN_COLORS = [
  [6554, 115, 10304, 28, 5702, 7756, 5681, 4510, -31835, 22437, 2859, -11339, 16, 5157, 10446, 3658, -27314, -21965, 472, 580, 784, 21966, 28950, -15697, -14002],
  [9104, 10275, 7595, 3610, 7975, 8526, 918, -26734, 24466, 10145, -6882, 5027, 1457, 16565, -30545, 25486, 24, 5392, 10429, 3673, -27335, -21957, 192, 687, 412, 21821, 28835, -15460, -14019],
  [],
  [],
  [],
] as const;

const BODY_COLOR_CHANNEL_INDEX: Record<BodyColorChannel, number> = {
  hair: 0,
  torso: 1,
  legs: 2,
  feet: 3,
  skin: 4,
};

export function createDefaultBaseCharacterAppearance(): BaseCharacterAppearance {
  return {
    gender: 'male',
    kitByAppearanceSlot: Object.fromEntries(
      BASE_APPEARANCE_SLOT_ORDER.map((slot) => [String(slot), MALE_LOOK_OPTIONS[slot][0]!]),
    ),
    bodyColors: {
      hair: 0,
      torso: 0,
      legs: 0,
      feet: 0,
      skin: 0,
    },
  };
}

export function cloneBaseCharacterAppearance(appearance: BaseCharacterAppearance): BaseCharacterAppearance {
  const clone: BaseCharacterAppearance = {
    gender: 'male',
    kitByAppearanceSlot: { ...appearance.kitByAppearanceSlot },
    bodyColors: { ...appearance.bodyColors },
  };
  if (appearance.viewerColors) {
    clone.viewerColors = { ...appearance.viewerColors };
  }
  return clone;
}

export function normalizeBaseCharacterAppearance(raw: unknown): BaseCharacterAppearance {
  const fallback = createDefaultBaseCharacterAppearance();
  if (!raw || typeof raw !== 'object') {
    return fallback;
  }

  const source = raw as Partial<BaseCharacterAppearance>;
  const kitByAppearanceSlot: Record<string, number> = {};
  for (const slot of BASE_APPEARANCE_SLOT_ORDER) {
    const selected = source.kitByAppearanceSlot?.[String(slot)];
    kitByAppearanceSlot[String(slot)] = normalizeKitSelection(slot, selected);
  }

  const bodyColors = {} as Record<BodyColorChannel, number>;
  for (const channel of BODY_COLOR_CHANNELS) {
    bodyColors[channel] = normalizeBodyColorSelection(channel, source.bodyColors?.[channel]);
  }
  const viewerColors = normalizeViewerColors(source);

  const normalized: BaseCharacterAppearance = {
    gender: 'male',
    kitByAppearanceSlot,
    bodyColors,
  };
  if (viewerColors) {
    normalized.viewerColors = viewerColors;
  }
  return normalized;
}

export function buildBaseCharacterSignature(appearance: BaseCharacterAppearance): string {
  return [
    ...BASE_APPEARANCE_SLOT_ORDER.map((slot) => `${slot}:${appearance.kitByAppearanceSlot[String(slot)] ?? 0}`),
    ...BODY_COLOR_CHANNELS.map((channel) => `${channel}:${appearance.bodyColors[channel]}`),
    ...BODY_COLOR_CHANNELS.map((channel) => `viewer:${channel}:${appearance.viewerColors?.[channel] ?? ''}`),
  ].join('|');
}

export function getBaseAppearanceOptions(slot: BaseAppearanceSlot): number[] {
  return [...MALE_LOOK_OPTIONS[slot]];
}

export function getAppearanceSlotLabel(slot: BaseAppearanceSlot): string {
  switch (slot) {
    case 0:
      return 'Hair';
    case 1:
      return 'Jaw';
    case 2:
      return 'Torso';
    case 3:
      return 'Arms';
    case 4:
      return 'Hands';
    case 5:
      return 'Legs';
    case 6:
      return 'Feet';
  }
}

export function getBodyColorLabel(channel: BodyColorChannel): string {
  return channel === 'skin'
    ? 'Skin'
    : channel.charAt(0).toUpperCase() + channel.slice(1);
}

export function cycleBaseAppearanceKit(
  appearance: BaseCharacterAppearance,
  slot: BaseAppearanceSlot,
  direction: -1 | 1,
): void {
  const options = MALE_LOOK_OPTIONS[slot];
  if (options.length === 0) {
    return;
  }

  const current = appearance.kitByAppearanceSlot[String(slot)] ?? options[0]!;
  const index = options.indexOf(current);
  const nextIndex = index >= 0
    ? mod(index + direction, options.length)
    : 0;
  appearance.kitByAppearanceSlot[String(slot)] = options[nextIndex]!;
}

export function setBaseAppearanceColor(
  appearance: BaseCharacterAppearance,
  channel: BodyColorChannel,
  value: number,
): void {
  appearance.bodyColors[channel] = normalizeBodyColorSelection(channel, value);
  delete appearance.viewerColors?.[channel];
}

export function getBodyColorSwatches(channel: BodyColorChannel): BodyColorSwatch[] {
  return BODY_COLOR_OPTIONS[channel].map((value) => ({
    value,
    color: hslToHex(resolveChannelPreviewHsl(channel, value)),
  }));
}

export function randomizeBaseCharacterAppearance(appearance: BaseCharacterAppearance, scope: 'all' | 'head' = 'all'): void {
  const slots = scope === 'head' ? ([0, 1] as const) : BASE_APPEARANCE_SLOT_ORDER;
  for (const slot of slots) {
    const options = MALE_LOOK_OPTIONS[slot];
    if (options.length === 0) continue;
    appearance.kitByAppearanceSlot[String(slot)] = options[Math.floor(Math.random() * options.length)] ?? options[0]!;
  }

  const channels = scope === 'head'
    ? (['hair', 'skin'] as const)
    : BODY_COLOR_CHANNELS;

  for (const channel of channels) {
    const options = BODY_COLOR_OPTIONS[channel];
    if (options.length === 0) continue;
    appearance.bodyColors[channel] = options[Math.floor(Math.random() * options.length)] ?? options[0]!;
    delete appearance.viewerColors?.[channel];
  }
}

export function resolveBaseCharacterColorSwaps(appearance: BaseCharacterAppearance): CharacterColorSwap[] {
  const swaps: CharacterColorSwap[] = [];

  for (const channel of BODY_COLOR_CHANNELS) {
    const channelIndex = BODY_COLOR_CHANNEL_INDEX[channel];
    const colorValue = appearance.bodyColors[channel];
    const bodySource = GAME0_SOURCE_BODY_COLORS[channelIndex];
    const bodyDestination = GAME0_DESTINATION_BODY_COLORS[channelIndex]?.[colorValue];
    if (typeof bodySource === 'number' && typeof bodyDestination === 'number') {
      swaps.push({ from: bodySource, to: bodyDestination });
    }

    const skinSource = GAME0_SOURCE_SKIN_COLORS[channelIndex];
    const skinDestination = GAME0_DESTINATION_SKIN_COLORS[channelIndex]?.[colorValue];
    if (typeof skinSource === 'number' && skinSource !== -1 && typeof skinDestination === 'number') {
      swaps.push({ from: skinSource, to: skinDestination });
    }
  }

  return swaps;
}

export function resolveBaseCharacterFaceColorOverrides(appearance: BaseCharacterAppearance): Record<number, [number, number, number]> {
  const overrides: Record<number, [number, number, number]> = {};
  for (const channel of BODY_COLOR_CHANNELS) {
    const rgb = parseHexColor(appearance.viewerColors?.[channel]);
    if (!rgb) {
      continue;
    }
    const channelIndex = BODY_COLOR_CHANNEL_INDEX[channel];
    addFaceColorOverride(overrides, GAME0_SOURCE_BODY_COLORS[channelIndex], rgb);
    addFaceColorOverride(overrides, GAME0_SOURCE_SKIN_COLORS[channelIndex], rgb);
  }
  return overrides;
}

function normalizeKitSelection(slot: BaseAppearanceSlot, selected: unknown): number {
  const options = MALE_LOOK_OPTIONS[slot];
  const acceptedOptions = [...options, ...(STAFF_ONLY_LOOK_OPTIONS[slot] ?? [])];
  const value = typeof selected === 'number' && Number.isFinite(selected)
    ? selected
    : options[0]!;
  return acceptedOptions.includes(value) ? value : options[0]!;
}

function normalizeBodyColorSelection(channel: BodyColorChannel, selected: unknown): number {
  const options = BODY_COLOR_OPTIONS[channel];
  const value = typeof selected === 'number' && Number.isFinite(selected)
    ? selected
    : options[0]!;
  return options.includes(value) ? value : options[0]!;
}

function normalizeViewerColors(source: Partial<BaseCharacterAppearance>): Partial<Record<BodyColorChannel, string>> | undefined {
  const viewer = source.viewerColors as Partial<Record<BodyColorChannel, unknown>> | undefined;
  const body = source.bodyColors as Partial<Record<BodyColorChannel, unknown>> | undefined;
  const normalized: Partial<Record<BodyColorChannel, string>> = {};
  for (const channel of BODY_COLOR_CHANNELS) {
    const value = viewer?.[channel] ?? body?.[channel];
    if (typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)) {
      normalized[channel] = value.toLowerCase();
    }
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function addFaceColorOverride(
  overrides: Record<number, [number, number, number]>,
  sourceHsl: number | undefined,
  rgb: [number, number, number],
): void {
  if (typeof sourceHsl !== 'number' || sourceHsl === -1) {
    return;
  }
  overrides[sourceHsl] = rgb;
  overrides[sourceHsl & 0xffff] = rgb;
}

function parseHexColor(hex: string | undefined): [number, number, number] | null {
  if (!hex || /^#[0-9a-fA-F]{6}$/.test(hex) !== true) {
    return null;
  }
  return [
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  ];
}

function resolveChannelPreviewHsl(channel: BodyColorChannel, value: number): number {
  const channelIndex = BODY_COLOR_CHANNEL_INDEX[channel];
  if (channel === 'skin') {
    return GAME0_DESTINATION_SKIN_COLORS[channelIndex]?.[value]
      ?? GAME0_DESTINATION_BODY_COLORS[channelIndex]?.[value]
      ?? 0;
  }

  return GAME0_DESTINATION_BODY_COLORS[channelIndex]?.[value] ?? 0;
}

function hslToHex(hsl: number): string {
  const [red, green, blue] = hslToRgbNormalized(hsl);
  const r = Math.max(0, Math.min(255, Math.round(red * 255)));
  const g = Math.max(0, Math.min(255, Math.round(green * 255)));
  const b = Math.max(0, Math.min(255, Math.round(blue * 255)));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// NOTE: This is intentionally a separate copy of `hslToRgbNormalized` from
// `../shell/characterAppearance`. The shell copy applies `round3` (quantize
// to 3 decimal places) to each channel, which is needed there because the
// results feed a string-keyed color-remap Map (round3 stabilizes the key).
// Here, the result feeds `hslToHex` above, which applies its own
// `Math.round(channel * 255)`. Layering round3 on top would double-quantize
// at byte boundaries: empirically, 19/151 real GAME0 body/skin HSL values
// flip ±1 byte per channel between the two paths (e.g. hsl=-27322 gives
// RGB 76/143/203 without round3 vs 76/144/203 with it). Keeping this local
// copy round3-free preserves the current swatch hex colors.
function hslToRgbNormalized(hsl: number): [number, number, number] {
  const hue = ((hsl >> 10) & 63) / 64;
  const saturation = ((hsl >> 7) & 7) / 8;
  const lightness = (hsl & 127) / 128;

  const brightness = lightness > 0.5
    ? lightness * (1 + saturation) - saturation * lightness + saturation
    : lightness * (1 + saturation);
  if (brightness <= 0) {
    return [0, 0, 0];
  }

  const t = 2 * lightness - brightness;
  const hueTimesSix = hue * 6;
  const channel = (value: number): number => {
    let sample = value;
    if (sample < 0) sample += 6;
    if (sample >= 6) sample -= 6;
    if (sample < 1) return t + (brightness - t) * sample;
    if (sample < 3) return brightness;
    if (sample < 4) return t + (brightness - t) * (4 - sample);
    return t;
  };

  return [channel(hueTimesSix + 2), channel(hueTimesSix), channel(hueTimesSix - 2)];
}

function toHex(value: number): string {
  return value.toString(16).padStart(2, '0');
}

function mod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}
